import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { thermalPrinter } from '../native/thermalPrinter.js';
import { IcoPrint, IcoBluetooth } from '../components/common/Icons.jsx';
import { usePrinterStore } from '../store/printerStore.js';
import { usarPlan } from '../lib/plan.js';
import { useTipsStore }    from '../store/tipsStore.js';
import { useAuthStore }    from '../store/authStore.js';
import { guardarAjusteComercio } from '../api/auth.api.js';
import { Capacitor } from '@capacitor/core';
import styles from './PrinterSetupScreen.module.css';

const isNative  = Capacitor.isNativePlatform();
const hasWebBT  = !isNative && 'bluetooth' in navigator;
const hasWebUSB = !isNative && 'usb' in navigator;
const isAndroid = isNative && Capacitor.getPlatform() === 'android';

const TRANSPORTE = { usb: 'USB', 'usb-android': 'USB', bt: 'Bluetooth', native: 'Bluetooth (BLE)', ble: 'Bluetooth' };

export default function PrinterSetupScreen() {
  const navigate        = useNavigate();
  const savedDeviceId   = usePrinterStore((s) => s.deviceId);
  const savedDeviceName = usePrinterStore((s) => s.deviceName);
  const savedTransport  = usePrinterStore((s) => s.transport);
  const setPrinter      = usePrinterStore((s) => s.setPrinter);
  const clearPrinter    = usePrinterStore((s) => s.clearPrinter);
  const plan             = usarPlan();
  const tipsEnabled      = useTipsStore((s) => s.enabled);
  const tipPresets       = useTipsStore((s) => s.presets);
  const setTipsEnabled   = useTipsStore((s) => s.setEnabled);
  const setTipPresets    = useTipsStore((s) => s.setPresets);
  const esAdmin          = useAuthStore((s) => s.user?.role) === 'admin';
  const actualizarTenant = useAuthStore((s) => s.actualizarTenant);
  const [guardandoCocina, setGuardandoCocina] = useState(false);
  const [errorCocina,     setErrorCocina]     = useState(null);

  const [scanning,    setScanning]    = useState(false);
  const [devices,     setDevices]     = useState([]);
  const [connecting,  setConnecting]  = useState(null); // deviceId en proceso
  const [error,       setError]       = useState(null);
  const [success,     setSuccess]     = useState(null);
  const [autoPrint,   setAutoPrint]   = useState(() => localStorage.getItem('pos_auto_print') === 'true');
  const [buscando,    setBuscando]    = useState(false);   // Android: USB + emparejadas
  const [buscado,     setBuscado]     = useState(false);
  const [avisoBt,     setAvisoBt]     = useState(null);
  const seen = useRef(new Set());

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    setAutoPrint(next);
    localStorage.setItem('pos_auto_print', String(next));
  };

  const [connected, setConnected] = useState(() => thermalPrinter.isConnected());

  useEffect(() => {
    thermalPrinter.init().catch(() => {});
    // Reconecta en silencio si ya había una impresora autorizada
    thermalPrinter
      .autoReconnect(savedDeviceId, savedTransport)
      .then((t) => { if (t) setConnected(true); })
      .catch(() => {});
    return () => { thermalPrinter.stopScan().catch(() => {}); };
  }, []);

  const startScan = async () => {
    if (!isNative && !hasWebBT) {
      setError('El emparejamiento Bluetooth no está disponible en este navegador. Usa Chrome en Android o la app instalada.');
      return;
    }
    setError(null);
    setSuccess(null);
    setDevices([]);
    seen.current.clear();
    setScanning(true);

    try {
      await thermalPrinter.scan((device) => {
        if (!seen.current.has(device.deviceId)) {
          seen.current.add(device.deviceId);
          setDevices((prev) => [...prev, device]);
        }
      }, 8000);
    } catch (err) {
      // NotFoundError = usuario canceló el selector — no es un error real
      if (err.name !== 'NotFoundError') {
        setError('Error al escanear: ' + (err.message ?? 'Verifica que el Bluetooth esté activado'));
      }
    } finally {
      if (isNative) {
        // La app nativa sigue emitiendo dispositivos 8s tras el scan()
        setTimeout(() => setScanning(false), 8000);
      } else {
        setScanning(false);
      }
    }
  };

  // Android: lo conectado por USB y lo emparejado por Bluetooth, en una lista.
  const buscarAndroid = async () => {
    setError(null);
    setSuccess(null);
    setBuscando(true);
    try {
      const { dispositivos, errorBluetooth } = await thermalPrinter.listarAndroid();
      setDevices(dispositivos);
      setAvisoBt(errorBluetooth);
      setBuscado(true);
    } finally {
      setBuscando(false);
    }
  };

  // Al volver de los ajustes de Bluetooth (de emparejar la impresora) la
  // lista se refresca sola: si no, el cajero vuelve y no la ve.
  const buscadoRef = useRef(false);
  buscadoRef.current = buscado;
  useEffect(() => {
    if (!isAndroid) return undefined;
    const alVolver = () => { if (document.visibilityState === 'visible' && buscadoRef.current) buscarAndroid(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, []);

  const startScanUsb = async () => {
    setError(null);
    setSuccess(null);
    try {
      await thermalPrinter.scanUsb((device) => {
        setDevices([device]);
        seen.current.clear();
        seen.current.add(device.deviceId);
      });
    } catch (err) {
      // NotFoundError = usuario cerró el selector sin elegir
      if (err.name !== 'NotFoundError') {
        setError('Error al buscar por USB: ' + (err.message ?? 'Intenta de nuevo'));
      }
    }
  };

  const handleConnect = async (device) => {
    setError(null);
    setConnecting(device.deviceId);
    const transport = device.transport ?? (isNative ? 'native' : 'ble');
    const name      = device.name ?? device.deviceId;
    try {
      if (transport === 'bt' || transport === 'usb-android') {
        await thermalPrinter.conectarAndroid(device);
      } else if (transport === 'usb') {
        await thermalPrinter.connectUsb(device.deviceId);
      } else {
        await thermalPrinter.connect(device.deviceId);
      }
      setPrinter(device.deviceId, name, transport);
      setConnected(true);
      setSuccess(`Impresora "${name}" conectada por ${TRANSPORTE[transport] ?? 'Bluetooth'}.`);
    } catch (err) {
      setError('No se pudo conectar: ' + (err.message ?? 'Intenta de nuevo'));
    } finally {
      setConnecting(null);
    }
  };

  // Es del comercio, no de esta tablet: se guarda en el servidor y le llega a
  // las demás al abrir la app o volver a ella.
  const toggleCocina = async () => {
    if (!esAdmin || guardandoCocina) return;
    const siguiente = !plan.cocina;
    setGuardandoCocina(true);
    setErrorCocina(null);
    try {
      const t = await guardarAjusteComercio({ posKitchenEnabled: siguiente });
      actualizarTenant({ posKitchenEnabled: t?.posKitchenEnabled ?? siguiente });
    } catch (err) {
      setErrorCocina(
        err.response?.data?.message ?? err.response?.data?.error
          ?? 'No se pudo guardar. Revisa que haya internet e intenta de nuevo.'
      );
    } finally {
      setGuardandoCocina(false);
    }
  };

  const handleClear = () => {
    thermalPrinter.disconnect().catch(() => {});
    clearPrinter();
    setConnected(false);
    setSuccess(null);
    setError(null);
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>

        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Ajustes de venta</h1>
        </div>

        {/* Dispositivo emparejado */}
        {savedDeviceId && (
          <div className={styles.paired}>
            <div className={styles.pairedIcon}><IcoPrint size={28} /></div>
            <div className={styles.pairedInfo}>
              <p className={styles.pairedName}>{savedDeviceName ?? savedDeviceId}</p>
              <p className={styles.pairedSub}>
                {TRANSPORTE[savedTransport] ?? 'Bluetooth'}
                {' · '}
                {connected ? 'Conectada' : 'Sin conexión — reconecta abajo'}
              </p>
            </div>
            <button className="btn btn--ghost" onClick={handleClear}>Quitar</button>
          </div>
        )}

        {/* Info web — sin ningún transporte disponible */}
        {!isNative && !hasWebBT && !hasWebUSB && (
          <div className={styles.webNote}>
            <p>Este navegador no permite conectar impresoras. Usa Chrome en Android o la app instalada en iOS/Android.</p>
            <p>Mientras tanto, el botón <strong>"Imprimir ticket"</strong> abre el diálogo de impresión del sistema.</p>
          </div>
        )}

        {/* Android: Bluetooth clásico y USB. La mayoría de las térmicas de 58 y
            80 mm son así; las de bajo consumo (BLE) van abajo, aparte. */}
        {isAndroid && (
          <>
            <div className={styles.instructions}>
              <p><strong>Por Bluetooth:</strong> primero emparéjala en los ajustes de Bluetooth de la tablet, una sola vez. Si pide PIN, suele ser 0000 o 1234.</p>
              <p><strong>Por USB:</strong> conéctala con el cable. Si Android pregunta si abre Geek Commerce POS con ella, elige <strong>Siempre</strong>.</p>
              <p>Luego toca <strong>Buscar impresoras</strong> y elige la tuya.</p>
            </div>
            <button className="btn btn--primary btn--full" onClick={buscarAndroid} disabled={buscando || !!connecting}>
              {buscando ? <><span className="spinner" /> Buscando…</> : 'Buscar impresoras'}
            </button>
            <button className="btn btn--secondary btn--full" onClick={() => thermalPrinter.abrirAjustesBluetooth().catch(() => {})}>
              <IcoBluetooth size={16} /> Abrir ajustes de Bluetooth
            </button>
          </>
        )}

        {/* Instrucciones */}
        {!isAndroid && (isNative || hasWebBT || hasWebUSB) && (
          <div className={styles.instructions}>
            <p>1. Enciende la impresora — por USB, conéctala al dispositivo.</p>
            {isNative
              ? <p>2. Presiona <strong>Escanear</strong> — la impresora aparecerá en la lista.</p>
              : <p>2. Elige cómo está conectada — selecciónala en el diálogo del navegador.</p>
            }
            <p>3. Tócala para conectar. Solo se hace una vez.</p>
            {hasWebUSB && !isNative && (
              <p className={styles.tip}>
                Por USB la impresora queda autorizada de forma permanente: se
                reconecta sola al abrir la app. Por Bluetooth puede que tengas
                que volver a elegirla tras recargar.
              </p>
            )}
          </div>
        )}

        {/* Botones de conexión. En Android, BLE queda como opción secundaria. */}
        {(isNative || hasWebBT) && (
          <button
            className={isAndroid ? `btn btn--ghost btn--full ${styles.bleBtn}` : 'btn btn--primary btn--full'}
            onClick={startScan}
            disabled={scanning}
          >
            {scanning
              ? <><span className="spinner" /> {isNative ? 'Escaneando…' : 'Abriendo selector…'}</>
              : isAndroid ? '¿No aparece? Buscar impresoras Bluetooth de bajo consumo (BLE)'
              : isNative ? 'Escanear dispositivos' : 'Buscar por Bluetooth'
            }
          </button>
        )}

        {hasWebUSB && !isNative && (
          <button
            className="btn btn--full"
            onClick={startScanUsb}
            disabled={scanning}
          >
            Buscar por USB
          </button>
        )}

        {/* Lista de dispositivos */}
        {devices.length > 0 && (
          <div className={styles.deviceList}>
            <p className={styles.listTitle}>Dispositivos encontrados</p>
            {devices.map((d) => (
              <button
                key={`${d.transport ?? ''}${d.deviceId}`}
                className={`${styles.deviceRow} ${d.impresora === false ? styles.deviceRowOtro : ''}`}
                onClick={() => handleConnect(d)}
                disabled={!!connecting}
              >
                <span className={styles.deviceIcon}>
                  {d.transport === 'usb' || d.transport === 'usb-android' ? <IcoPrint size={16} /> : <IcoBluetooth size={16} />}
                </span>
                <span className={styles.deviceName}>{d.name ?? 'Dispositivo desconocido'}</span>
                <span className={styles.deviceId}>
                  {d.transport ? (TRANSPORTE[d.transport] ?? d.transport) : d.deviceId.slice(0, 8)}
                </span>
                {connecting === d.deviceId && <span className="spinner" />}
              </button>
            ))}
          </div>
        )}

        {isAndroid && buscado && !buscando && devices.length === 0 && (
          <p className={styles.hint}>
            No encontré impresoras. Si es Bluetooth, emparéjala primero en los ajustes de Bluetooth; si es USB, revisa el cable y que esté encendida.
          </p>
        )}
        {isAndroid && avisoBt && <p className={styles.error}>{avisoBt}</p>}

        {scanning && devices.length === 0 && (
          <p className={styles.hint}>Buscando impresoras cercanas…</p>
        )}

        {error   && <p className={styles.error}>{error}</p>}
        {success && <p className={styles.successMsg}>{success}</p>}

        {/* Cocina: del comercio. Solo restaurantes (servicio en mesa). */}
        {plan.servicioEnMesa && (
          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <p className={styles.settingLabel}>Mandar a cocina</p>
              <p className={styles.settingHint}>
                Marchar comandas, tiempos y pantalla de cocina. Apágalo si una sola tablet toma la orden y cobra.
                {!esAdmin && ' Lo cambia un administrador.'}
              </p>
              {errorCocina && <p className={styles.error}>{errorCocina}</p>}
            </div>
            <button
              className={`${styles.toggle} ${plan.cocina ? styles.toggleOn : ''}`}
              onClick={toggleCocina}
              role="switch"
              aria-checked={plan.cocina}
              aria-label="Mandar a cocina"
              disabled={!esAdmin || guardandoCocina}
            />
          </div>
        )}

        {/* Auto-print toggle */}
        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <p className={styles.settingLabel}>Auto-imprimir al cobrar</p>
            <p className={styles.settingHint}>Imprime el ticket automáticamente al completar una venta</p>
          </div>
          <button
            className={`${styles.toggle} ${autoPrint ? styles.toggleOn : ''}`}
            onClick={toggleAutoPrint}
            role="switch"
            aria-checked={autoPrint}
            aria-label="Auto-imprimir al cobrar"
          />
        </div>

        {/* Propinas: el ajuste solo existe si el plan del comercio las trae */}
        {plan.propinas && (
        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <p className={styles.settingLabel}>Pedir propina al cobrar</p>
            <p className={styles.settingHint}>Muestra botones de propina en el checkout. Actívalo en cafeterías y restaurantes.</p>
          </div>
          <button
            className={`${styles.toggle} ${tipsEnabled ? styles.toggleOn : ''}`}
            onClick={() => setTipsEnabled(!tipsEnabled)}
            role="switch"
            aria-checked={tipsEnabled}
            aria-label="Pedir propina al cobrar"
          />
        </div>
        )}

        {plan.propinas && tipsEnabled && (
          <div className={styles.instructions}>
            <p style={{ fontWeight: 600, color: 'var(--text-1)' }}>Porcentajes sugeridos</p>
            <p>Los que verá el cajero como botones rápidos. Siempre puede capturar otro monto.</p>
            <div className={styles.tipPresetRow}>
              {tipPresets.map((p, idx) => (
                <div key={idx} className={styles.tipPresetField}>
                  <input
                    className="input"
                    type="number"
                    inputMode="decimal"
                    min={1}
                    max={100}
                    value={p}
                    onChange={(e) => {
                      const next = [...tipPresets];
                      next[idx] = e.target.value;
                      setTipPresets(next);
                    }}
                  />
                  <span className={styles.tipPresetPct}>%</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
